import { createSlice, isAnyOf } from '@reduxjs/toolkit';
import MojitoCore from 'mojito/core';
import MojitoServices from 'mojito/services';
import MojitoModules from 'mojito/modules';
import MojitoPresentation from 'mojito/presentation';
import { SLIDING_VIEW_TYPES } from 'applications/sports/app/view/types.js';
import ApplicationTypes from './types.js';
import { isQuickBetslipActive as isQuickBetslip } from './utils.js';

const {
    ADD_FIRST_SELECTION,
    ADD_FIRST_PREPLAY_SELECTION,
    ADD_FIRST_INPLAY_SELECTION,
    CONTAINS_MAX_ACCUMULATOR,
    CONTAINS_ONE_BET_BUILDER,
    ADD_MATCH_ACCA,
} = ApplicationTypes.SHOW_BETSLIP_CONDITION;
const { BETSLIP_TYPE } = MojitoModules.SlidingBetslip.types;
const { selectEventItem } = MojitoServices.SportsContent.Events.selectors;
const { actions: keypadActions, selectors: keypadSelectors } = MojitoModules.Keypad;
const loginHelper = MojitoModules.Login.helper;
const {
    actions: betslipActions,
    utils: BetslipUtils,
    selectors: { selectBetslip, selectBetslipSettings, selectQuickBetslipManuallyClosed },
} = MojitoServices.Betslip;
const { selectApplicationMode } = MojitoCore.Services.SystemSettings.selectors;
const { APPLICATION_MODE } = MojitoCore.Services.SystemSettings.types;
const BetsTypes = MojitoServices.Bets.types;
const { actions: accaBarActions } = MojitoModules.AccaBar;

/**
 * The name of the application settings store. Will be used to register in global redux store.
 *
 * @constant
 * @type {string}
 * @memberof Mojito.Applications.Sports
 */
export const STORE_KEY = 'application/main';

const {
    SHOW_SLIDING_BETSLIP,
    SHOW_SLIDING_OPEN_BETS,
    SHOW_SLIDING_AZ_MENU,
    HIDE_SLIDING_BETSLIP,
    HIDE_SLIDING_OPEN_BETS,
    HIDE_SLIDING_AZ_MENU,
    VIEW,
    MOJITO_INITIALIZED,
    BACK_NAVIGATION,
    OVERLAY_INTERACTION,
    SHOW_FOREIGN_WINDOW,
    HIDE_FOREIGN_WINDOW,
    SHOW_ACCOUNT_POPUP,
    HIDE_ACCOUNT_POPUP,
} = MojitoPresentation.Base.Intent.Types;

const INTENT_TO_SLIDING_VIEW_TYPE = {
    [SHOW_SLIDING_BETSLIP]: SLIDING_VIEW_TYPES.BETSLIP,
    [SHOW_SLIDING_OPEN_BETS]: SLIDING_VIEW_TYPES.OPEN_BETS,
    [SHOW_SLIDING_AZ_MENU]: SLIDING_VIEW_TYPES.AZ_MENU,
    [SHOW_FOREIGN_WINDOW]: SLIDING_VIEW_TYPES.FOREIGN_WINDOW,
    [SHOW_ACCOUNT_POPUP]: SLIDING_VIEW_TYPES.MY_ACCOUNT,
};

const SHOW_SLIDING_VIEW_INTENTS = [
    SHOW_SLIDING_BETSLIP,
    SHOW_SLIDING_OPEN_BETS,
    SHOW_SLIDING_AZ_MENU,
    SHOW_FOREIGN_WINDOW,
    SHOW_ACCOUNT_POPUP,
];

const HIDE_SLIDING_VIEW_INTENTS = [
    HIDE_SLIDING_BETSLIP,
    HIDE_SLIDING_OPEN_BETS,
    HIDE_SLIDING_AZ_MENU,
    HIDE_FOREIGN_WINDOW,
    VIEW,
    BACK_NAVIGATION,
    OVERLAY_INTERACTION,
    HIDE_ACCOUNT_POPUP,
];

const STORABLE_SLIDING_VIEWS = [SLIDING_VIEW_TYPES.BETSLIP];

const { NamedService } = MojitoCore.Services.Storage;
const reduxInstance = MojitoCore.Services.redux;
const intentActions = MojitoCore.Intents.actions;

const showBetslipConditions = {
    [ADD_FIRST_SELECTION]: (_, betslip) => hasOneSingleBet(betslip),
    [ADD_FIRST_INPLAY_SELECTION]: (event, betslip) =>
        hasOneSingleBet(betslip) && event.details.inPlay,
    [ADD_FIRST_PREPLAY_SELECTION]: (event, betslip) =>
        hasOneSingleBet(betslip) & !event.details.inPlay,
    [CONTAINS_MAX_ACCUMULATOR]: (_, betslip) => {
        const betBuilderBets = BetslipUtils.getSingleBets(
            betslip,
            legSort =>
                legSort === BetsTypes.LEG_SORT.BET_BUILDER ||
                legSort === BetsTypes.LEG_SORT.MATCH_ACCA
        );
        if (betBuilderBets.length > 0) {
            return false;
        }

        const singleBets = BetslipUtils.getSingleBets(betslip);
        if (singleBets.length > 1) {
            const accumulatorType = BetsTypes.RANKED_BY_INDEX[singleBets.length];
            return BetslipUtils.getBetsByType(betslip, accumulatorType)[0];
        }
    },
    [CONTAINS_ONE_BET_BUILDER]: (_, betslip) => {
        const betBuilderBets = BetslipUtils.getSingleBets(
            betslip,
            legSort => legSort === BetsTypes.LEG_SORT.BET_BUILDER
        );
        return (
            betBuilderBets.length === 1 &&
            BetslipUtils.getSingleBetsCount(betslip) === betBuilderBets[0].parts.length &&
            !!betBuilderBets[0].odds // Having no odds implies uncombinable selections
        );
    },
    [ADD_MATCH_ACCA]: (_, betslip, payload) =>
        payload?.requestPayload?.legSort === BetsTypes.LEG_SORT.MATCH_ACCA,
};

const applicationStorageService = new NamedService('activeOverlay');
const storedActiveView = applicationStorageService.getItem() || {};

/**
 * Defines the state of the application store.
 *
 * @typedef ApplicationState
 * @property {boolean} isInitialized - True if application is initialized.
 * @property {{betslipAutoShowUp: object}} config - Configuration object.
 * Property betslipAutoShowUp lists the conditions to show sliding betslip (a standard or quick betlsip).
 * Key is {Mojito.Application.ApplicationTypes.SHOW_BETSLIP_CONDITION},
 * value is {Mojito.Modules.SlidingBetslip.types.BETSLIP_TYPE}.
 * The conditions are checked in the order as properties are listed.
 * A first met condition triggers a sliding betslip of a corresponding type.
 * If no conditions are met, sliding betslip is not rendered.
 * @property {string|undefined} activeSlidingViewType - Active sliding view type or undefined.
 * @property {string|undefined} activeSlidingViewData - Active sliding view data or undefined.
 * @property {boolean} keypadIsActive - True if keypad is active.
 * @memberof Mojito.Application
 */

export const initialState = {
    config: {
        betslipAutoShowUp: {
            [ADD_FIRST_SELECTION]: BETSLIP_TYPE.QUICK_BETSLIP,
            [CONTAINS_MAX_ACCUMULATOR]: BETSLIP_TYPE.QUICK_BETSLIP,
            [CONTAINS_ONE_BET_BUILDER]: BETSLIP_TYPE.QUICK_BETSLIP,
        },
    },
    activeSlidingViewType: storedActiveView.type,
    activeSlidingViewData: storedActiveView.data,
    keypadIsActive: keypadSelectors.selectIsActive(),
};

export const { reducer, actions } = createSlice({
    name: 'application',
    initialState,
    reducers: {
        configureApplicationSettings: (state, { payload }) => {
            state.config = payload;
        },
        reset: () => initialState,
        clearActiveViewSuccess(state) {
            state.activeSlidingViewType = undefined;
            state.activeSlidingViewData = undefined;
        },
        activeKeypad(state, { payload }) {
            state.keypadIsActive = payload;
        },
        activeSlidingType(state, { payload }) {
            state.activeSlidingViewType = payload;
        },
        activeSlidingData(state, { payload }) {
            state.activeSlidingViewData = payload;
        },
        isInitializedSuccess(state) {
            state.isInitialized = true;
        },
    },
});

/**
 * Application store related actions.
 *
 * @name actions
 * @memberof Mojito.Applications.Sports
 */

/**
 * Clear active view success.
 *
 * @function clearActiveViewSuccess
 * @type {Mojito.Core.Services.redux.ActionCreator}
 *
 * @memberof Mojito.Applications.Sports.actions
 */

/**
 * Clear active view success.
 *
 * @function activeKeypad
 * @type {Mojito.Core.Services.redux.ActionCreator}
 * @param {boolean} payload - Keypad is active value.
 *
 * @memberof Mojito.Applications.Sports.actions
 */

/**
 * Update active sliding view type state.
 *
 * @function activeSlidingType
 * @type {Mojito.Core.Services.redux.ActionCreator}
 * @param {string|undefined} payload - Active sliding type value or undefined.
 *
 * @memberof Mojito.Applications.Sports.actions
 */

/**
 * Update active sliding view data state.
 *
 * @function activeSlidingData
 * @type {Mojito.Core.Services.redux.ActionCreator}
 * @param {object} payload - Intent data.
 *
 * @memberof Mojito.Applications.Sports.actions
 */

/**
 * This action is called for initial configuration setup for application layer.
 *
 * @function configure
 *
 * @param {object} payload - Initial configuration.
 * @returns {Mojito.Core.Services.redux.ThunkFunction} Init thunk. Dispatches restoreSession action once init is done.
 * @memberof Mojito.Applications.Sports.actions
 */
actions.configure = payload => (dispatch, getState) => {
    const storeConfig = payload?.application?.stores?.application || {};
    if (storeConfig.betslipAutoShowUp) {
        dispatch(actions.configureApplicationSettings(storeConfig));
    }
    const state = getState();
    const betslipAutoShowUp =
        storeConfig.betslipAutoShowUp || state[STORE_KEY].config.betslipAutoShowUp;
    dispatch(accaBarActions.configure(getAccaBarConfig(betslipAutoShowUp, state)));
};

/**
 * This action is an interface for clearActiveViewSuccess.
 *
 * @function clearActiveView
 *
 * @returns {Mojito.Core.Services.redux.ThunkFunction} Init thunk. Dispatches restoreSession action once init is done.
 * @memberof Mojito.Applications.Sports.actions
 */
actions.clearActiveView = () => dispatch => {
    dispatch(actions.clearActiveViewSuccess());
    applicationStorageService.removeItem();
};

/**
 * Action to store active view.
 *
 * @function storeActiveView
 *
 * @returns {Mojito.Core.Services.redux.ThunkFunction} Init thunk. Dispatches restoreSession action once init is done.
 * @memberof Mojito.Applications.Sports.actions
 */
actions.storeActiveView = () => (dispatch, getState) => {
    const { activeSlidingViewType, activeSlidingViewData } = getState()[STORE_KEY];
    if (!STORABLE_SLIDING_VIEWS.includes(activeSlidingViewType)) {
        return;
    }
    const data = {
        type: activeSlidingViewType,
        data: activeSlidingViewData,
    };
    applicationStorageService.setItem(data);
};

reduxInstance.actionListener.startListening({
    matcher: isAnyOf(
        betslipActions.addPartSuccess,
        betslipActions.addCompositeSuccess,
        betslipActions.removeCompositeSuccess,
        betslipActions.removePartSuccess,
        betslipActions.clearBetslipSuccess
    ),
    effect: (action, listenerApi) => {
        // Here to decide what show in sliding view (used on mobile):
        // a standard betslip or a quick betslip or no sliding betslip at all.
        const betslipType = getBetslipTypeToShow(listenerApi.getState(), action.payload);
        if (betslipType) {
            listenerApi.dispatch(actions.activeSlidingType(SLIDING_VIEW_TYPES.BETSLIP));
            listenerApi.dispatch(actions.activeSlidingData({ betslipType }));
            listenerApi.dispatch(actions.storeActiveView());
        } else if (
            listenerApi.getState()[STORE_KEY].activeSlidingViewType === SLIDING_VIEW_TYPES.BETSLIP
        ) {
            listenerApi.dispatch(actions.activeSlidingType(undefined));
            // Not using clearActiveView here because if we clean activeSlidingViewData immediately, the component is
            // fast enough to apply REGULAR_BETSLIP mode which is set by default, Which lead to undesired jumping.
            applicationStorageService.removeItem();
        }
    },
});

reduxInstance.actionListener.startListening({
    actionCreator: betslipActions.initSuccess,
    effect: (action, listenerApi) => {
        if (
            listenerApi.getState()[STORE_KEY].activeSlidingViewType !== SLIDING_VIEW_TYPES.BETSLIP
        ) {
            return;
        }

        const betslip = selectBetslip(listenerApi.getState());
        const selectionsCount = BetslipUtils.getSingleBetsCount(betslip);
        if (selectionsCount === 0) {
            listenerApi.dispatch(actions.clearActiveView());
        }
    },
});

reduxInstance.actionListener.startListening({
    actionCreator: betslipActions.placeBetslipSuccess,
    effect: (action, listenerApi) => {
        const { status } = action.payload;
        if (
            BetslipUtils.isOverask(status) &&
            isQuickBetslipActive(listenerApi.getState()[STORE_KEY])
        ) {
            listenerApi.dispatch(
                actions.activeSlidingData({ betslipType: BETSLIP_TYPE.STANDARD_BETSLIP })
            );
            listenerApi.dispatch(actions.storeActiveView());
        }
    },
});

reduxInstance.actionListener.startListening({
    matcher: isAnyOf(keypadActions.setInputId, keypadActions.inputBlur),
    effect: (action, listenerApi) => {
        listenerApi.dispatch(
            actions.activeKeypad(keypadSelectors.selectIsActive(listenerApi.getState()))
        );
    },
});

reduxInstance.actionListener.startListening({
    actionCreator: intentActions.publishIntent,
    effect: (action, listenerApi) => {
        const intent = action.payload;
        const globalState = listenerApi.getState();
        const { keypadIsActive, activeSlidingViewType } = globalState[STORE_KEY];
        // Keypad is treated as overlay, so if it is active during BACK_NAVIGATION, we will mark it as deactivated.
        // We will not hide any other activeSlidingView in this case.
        if (needToHideKeypad(intent.type, keypadIsActive)) {
            listenerApi.dispatch(actions.activeKeypad(false));
            return;
        }
        if (needToNavigateBack(intent.type, activeSlidingViewType)) {
            window.history.back();
            return;
        }
        if (needToShowSlidingView(intent.type)) {
            // we don't show sliding view if published intent requires user to be authenticated
            const type = loginHelper.isLoginRequired(intent.type)
                ? undefined
                : INTENT_TO_SLIDING_VIEW_TYPE[intent.type];

            let activeSlidingData = intent.data;
            if (type === SLIDING_VIEW_TYPES.BETSLIP) {
                listenerApi.dispatch(betslipActions.resetQuickBetslipManuallyClosed());

                // If conditions are met, show quick bet slip.
                // However, if already in QBS, switch to standard betslip as requested by user.
                const betslipType = isQuickBetslipActive(globalState[STORE_KEY])
                    ? BETSLIP_TYPE.STANDARD_BETSLIP
                    : getBetslipTypeToShow(globalState, undefined, true);
                activeSlidingData = { betslipType: betslipType || BETSLIP_TYPE.STANDARD_BETSLIP };
            }
            listenerApi.dispatch(actions.activeSlidingType(type));
            listenerApi.dispatch(actions.activeSlidingData(activeSlidingData));
            listenerApi.dispatch(actions.storeActiveView());
        } else if (needToHideSlidingView(intent.type)) {
            listenerApi.dispatch(actions.activeSlidingType(undefined));
            // Not using clearActiveView here because if we clean activeSlidingViewData immediately, the component is
            // fast enough to apply REGULAR_BETSLIP mode which is set by default, which leads to undesired jumping.
            applicationStorageService.removeItem();
        } else if (intent.type === MOJITO_INITIALIZED) {
            listenerApi.dispatch(actions.isInitializedSuccess());
        }
    },
});

reduxInstance.actionListener.startListening({
    actionCreator: betslipActions.closeQuickBetslipManually,
    effect: (_, listenerApi) => {
        // When QBS was closed manually, acca bar shown for all cases
        const accaBarConfig = {
            accumulatorEnabled: true,
            betBuilderEnabled: true,
        };
        listenerApi.dispatch(accaBarActions.configure(accaBarConfig));
    },
});

reduxInstance.actionListener.startListening({
    matcher: isAnyOf(
        betslipActions.resetQuickBetslipManuallyClosed,
        betslipActions.betslipSettingsUpdate
    ),
    effect: (_, listenerApi) => {
        const state = listenerApi.getState();
        const { betslipAutoShowUp } = state[STORE_KEY].config;
        listenerApi.dispatch(accaBarActions.configure(getAccaBarConfig(betslipAutoShowUp, state)));
    },
});

const needToHideKeypad = (intentType, keypadIsActive) =>
    intentType === BACK_NAVIGATION && keypadIsActive;
const needToNavigateBack = (intentType, activeSlidingViewType) =>
    intentType === BACK_NAVIGATION && !activeSlidingViewType;
const needToShowSlidingView = intentType => SHOW_SLIDING_VIEW_INTENTS.includes(intentType);
const needToHideSlidingView = intentType => HIDE_SLIDING_VIEW_INTENTS.includes(intentType);
const isQuickBetslipActive = state =>
    isQuickBetslip(state.activeSlidingViewType, state.activeSlidingViewData);
const isStandardSlidingBetslipActive = state =>
    state.activeSlidingViewType === SLIDING_VIEW_TYPES.BETSLIP &&
    state.activeSlidingViewData?.betslipType === BETSLIP_TYPE.STANDARD_BETSLIP;

const getBetslipTypeToShow = (state, payload, ignoreManuallyClosed) => {
    const { config } = state[STORE_KEY];
    const { betslipAutoShowUp } = config;
    const betslip = selectBetslip(state);
    const singleBets = BetslipUtils.getSingleBets(betslip);

    // Sliding betslip can only be used on mobile when betslip has selections
    if (selectApplicationMode(state) === APPLICATION_MODE.DESKTOP || singleBets.length === 0) {
        return;
    }

    // If already in a sliding standard betslip, do not switch to a quick betslip
    if (isStandardSlidingBetslipActive(state[STORE_KEY])) {
        return BETSLIP_TYPE.STANDARD_BETSLIP;
    }

    const bet = singleBets[singleBets.length - 1];
    const eventId = BetslipUtils.getEventIdFromBet(bet);
    const event = selectEventItem(eventId, state) || {};

    const matchedCondition = Object.keys(betslipAutoShowUp).find(condition =>
        showBetslipConditions[condition](event, betslip, payload)
    );
    const betslipType = matchedCondition && betslipAutoShowUp[matchedCondition];
    if (
        betslipType === BETSLIP_TYPE.QUICK_BETSLIP &&
        (!selectBetslipSettings(state).quickBetslipEnabled ||
            (!ignoreManuallyClosed && selectQuickBetslipManuallyClosed(state)))
    ) {
        return;
    }

    return betslipType;
};

const hasOneSingleBet = betslip => {
    const singleBets = BetslipUtils.getSingleBets(betslip);
    // On Quantum it can be 1 single selection which is match acca, not a "real" single bet
    // Multicast is a normal single though.
    return singleBets.length === 1 && singleBets[0].legSort !== BetsTypes.LEG_SORT.MATCH_ACCA;
};

// Acca bar is enabled if no sliding betslip is configured for the certain action or QBS is disabled
const getAccaBarConfig = (betslipAutoShowUp, state) => {
    const { quickBetslipEnabled } = selectBetslipSettings(state);
    return {
        accumulatorEnabled: !quickBetslipEnabled || !betslipAutoShowUp[CONTAINS_MAX_ACCUMULATOR],
        betBuilderEnabled:
            !quickBetslipEnabled ||
            (!betslipAutoShowUp[CONTAINS_ONE_BET_BUILDER] && !betslipAutoShowUp[ADD_MATCH_ACCA]),
    };
};

reduxInstance.injectReducer(STORE_KEY, reducer);
